import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import PersonaAvatar from '../../components/PersonaAvatar'
import { timeAgo } from '../../hooks/useTimeAgo'

interface CommunityFull {
  id: string; name: string; slug: string; type: string; accent_color?: string;
  created_at?: string; users?: CommunityUser[]; conversation_count?: number;
  last_message_at?: string; last_message_by?: string;
}
interface CommunityUser {
  id: string; name: string; email?: string; role: string; is_human?: boolean; is_persona?: boolean;
}

const TYPE_COLORS: Record<string, string> = {
  client: '#3b82f6', partner: '#8b5cf6', personal: '#3fb950', gathering: '#d29922',
}

export default function CommunitiesPage() {
  const [selectedCommunity, setSelectedCommunity] = useState<string | null>(null)
  const [tab, setTab] = useState<'conversations' | 'users' | 'settings'>('conversations')

  const { data: communities, isLoading } = useQuery({
    queryKey: ['communities'],
    queryFn: async () => {
      const res = await fetch('/api/communities')
      const data = await res.json()
      return (Array.isArray(data) ? data : data.communities || []) as CommunityFull[]
    },
  })

  const { data: communityDetail } = useQuery({
    queryKey: ['communityDetail', selectedCommunity],
    queryFn: async () => {
      const res = await fetch(`/api/communities/${selectedCommunity}`)
      return res.json() as Promise<CommunityFull>
    },
    enabled: !!selectedCommunity,
  })

  const { data: communityUsers } = useQuery({
    queryKey: ['communityUsers', selectedCommunity],
    queryFn: async () => {
      const res = await fetch(`/api/communities/${selectedCommunity}/users`)
      const data = await res.json()
      // Normalize: API may return plain array or { users: [...] }
      return { users: Array.isArray(data) ? data : (data.users || []) }
    },
    enabled: !!selectedCommunity && tab === 'users',
  })

  const { data: communityConvos } = useQuery({
    queryKey: ['communityConvos', selectedCommunity],
    queryFn: async () => {
      const res = await fetch(`/api/conversations?community=${selectedCommunity}`)
      const data = await res.json()
      const convs = Array.isArray(data) ? data : (data.conversations || [])
      // Normalize participants
      for (const c of convs) {
        if (c.participants?.length && typeof c.participants[0] === 'string') {
          c.participants = c.participants.map((p: string) => ({ persona: p }))
        }
      }
      return { conversations: convs }
    },
    enabled: !!selectedCommunity && tab === 'conversations',
  })

  if (isLoading) return <div className="p-6 text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</div>

  const allCommunities = communities || []
  const grouped: Record<string, CommunityFull[]> = {}
  for (const p of allCommunities) {
    const t = p.type || 'other'
    if (!grouped[t]) grouped[t] = []
    grouped[t].push(p)
  }

  return (
    <div className="flex h-full">
      {/* Community list */}
      <div className="w-72 shrink-0 overflow-y-auto py-4 px-3" style={{ borderRight: '1px solid var(--border)' }}>
        <h1 className="text-sm font-semibold mb-4 px-2" style={{ color: 'var(--text-primary)' }}>Communities</h1>
        {Object.entries(grouped).map(([type, list]) => (
          <section key={type} className="mb-4">
            <h2 className="text-xs font-semibold uppercase tracking-wider mb-1.5 px-2 capitalize" style={{ color: TYPE_COLORS[type] || 'var(--text-muted)' }}>
              {type} ({list.length})
            </h2>
            {list.map(p => (
              <button
                key={p.id}
                onClick={() => { setSelectedCommunity(p.id); setTab('conversations') }}
                className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-md text-xs transition-colors mb-0.5"
                style={{
                  background: selectedCommunity === p.id ? 'var(--bg-elevated)' : 'transparent',
                  color: selectedCommunity === p.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                }}
              >
                <div className="w-2 h-2 rounded-full shrink-0" style={{ background: p.accent_color || TYPE_COLORS[type] || '#6b7280' }} />
                {p.name}
              </button>
            ))}
          </section>
        ))}
      </div>

      {/* Community detail */}
      <div className="flex-1 overflow-y-auto">
        {selectedCommunity && communityDetail ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 rounded-full" style={{ background: communityDetail.accent_color || '#6b7280' }} />
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{communityDetail.name}</h2>
              <span className="text-xs capitalize px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
                {communityDetail.type}
              </span>
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`/api/community-view/${communityDetail.slug}`)
                    const data = await res.json()
                    if (data.token) {
                      // Save operator JWT so we can restore it later
                      const currentJwt = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
                      if (currentJwt) localStorage.setItem('operator-jwt', currentJwt)
                      // Set community token and reload in same tab
                      localStorage.setItem('session_token', data.token)
                      document.cookie = `session=${data.token}; path=/; max-age=86400; SameSite=Lax`
                      window.location.href = '/'
                    }
                  } catch (e) { console.error('Community view failed:', e) }
                }}
                className="text-xs px-3 py-1 rounded-md ml-auto"
                style={{ background: communityDetail.accent_color || 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer' }}
              >
                View Community →
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-4" style={{ borderBottom: '1px solid var(--border)' }}>
              {(['conversations', 'users', 'settings'] as const).map(t => (
                <button key={t} onClick={() => setTab(t)} className="text-xs px-3 py-2 capitalize" style={{
                  color: tab === t ? 'var(--text-primary)' : 'var(--text-muted)',
                  borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
                }}>
                  {t}
                </button>
              ))}
            </div>

            {tab === 'conversations' && (
              <div className="flex flex-col gap-1.5">
                {(communityConvos?.conversations || []).map((c: { id: string; title: string; last_message_at?: string; participants: { persona: string }[] }) => (
                  <Link key={c.id} to={`/conversation/${c.id}`} className="flex items-center gap-2 px-3 py-2.5 rounded-md no-underline hover:bg-[var(--bg-elevated)]" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                    <span className="text-xs font-medium flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{c.title || c.id}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      {(c.participants || []).slice(0, 5).map((p: any) => {
                        const name = (p.display_name || p.persona || p.user_id || '').toLowerCase()
                        const col = ({ forge: '#B25943', mira: '#9C59F5', flux: '#33C7A0', joel: '#59C5FA', timbre: '#ED9429', bianca: '#D4A054' } as Record<string, string>)[name] || '#8b949e'
                        const label = p.display_name || p.persona || p.user_id || '?'
                        return (
                          <span key={p.user_id || label} className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: col + '22', color: col, border: `1px solid ${col}44`, fontSize: 10 }}>
                            {label.charAt(0).toUpperCase() + label.slice(1)}
                          </span>
                        )
                      })}
                    </div>
                    {c.last_message_at && <span className="text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>{timeAgo(c.last_message_at)}</span>}
                  </Link>
                ))}
              </div>
            )}

            {tab === 'users' && (
              <div className="flex flex-col gap-1.5">
                {(communityUsers?.users || []).map((u: CommunityUser) => (
                  <div key={u.id} className="flex items-center gap-3 px-3 py-2 rounded-md" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                    <PersonaAvatar name={u.name} size={24} />
                    <div className="flex-1">
                      <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{u.name}</div>
                      {u.email && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{u.email}</div>}
                    </div>
                    <span className="text-xs capitalize" style={{ color: 'var(--text-secondary)' }}>{u.role}</span>
                    {u.is_persona && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--accent)' }}>persona</span>}
                    {u.is_human && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--success)' }}>human</span>}
                  </div>
                ))}
              </div>
            )}

            {tab === 'settings' && (
              <div className="p-4 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span style={{ color: 'var(--text-muted)' }}>ID</span><span style={{ color: 'var(--text-primary)' }}>{communityDetail.id}</span>
                  <span style={{ color: 'var(--text-muted)' }}>Slug</span><span style={{ color: 'var(--text-primary)' }}>{communityDetail.slug}</span>
                  <span style={{ color: 'var(--text-muted)' }}>Type</span><span style={{ color: 'var(--text-primary)' }} className="capitalize">{communityDetail.type}</span>
                  <span style={{ color: 'var(--text-muted)' }}>Accent</span><span style={{ color: 'var(--text-primary)' }}>{communityDetail.accent_color || 'default'}</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Select a community</p>
          </div>
        )}
      </div>
    </div>
  )
}
